import { computedStruct, makeAutoObservable } from 'mobx';
import { token } from '@/infrastructure/container';
import type { ICoachGateway } from '@/domain/coachGateway';
import { DomainError, OfflineError, ServerError, UpdateRequiredError } from '@/domain/errors';
import {
  courtCounts,
  latestMoveList,
  playerAttributes,
  playerRecord,
  sessionStatus,
  struckOffPlayers,
  type Attribute,
  type Court,
  type MoveRow,
  type PlayerNumber,
  type SessionEvent,
} from '@/domain/session';
import { UuidV7Gen, type IIdGen } from '@/services/IdGen';
import type { ILogger } from '@/services/Logger';

// The mover's whole screen, pinned by session/contracts/p4-rotation.md (Surface C) and by the
// blind oracle beside this file, which the implementation may not edit.
//
// The mover walks the hall and makes the tick's moves happen, because coaches are distracted and
// will not (docs/rotation.md). This store computes nothing about who should move: the admin device
// publishes the list and this screen registers what was actioned.
//
// The gateway is ICoachGateway itself rather than a new interface. The mover needs exactly what a
// coach needs — one `occ: false` execute, the folded state, and whether the far side is answering
// — and a second interface with the same three members would be two things to keep in step.

// One row of the audit view: every active player and the court they are on, which is the spot
// check a mover does between ticks (docs/rotation.md, "The mover's list").
export interface MoverAuditRow {
  readonly number: PlayerNumber;
  readonly court: Court;
}

// One row of the open court's list: what a coach sees on their own screen, for the same court.
// The two flags are here because the mover marks on a coach's behalf exactly as they vote on a
// coach's behalf (docs/scoring.md), and a mark button that could not show its current state would
// make the mover tap to find out — which sets the flag they were asking about.
export interface MoverCourtRow {
  readonly number: PlayerNumber;
  readonly score: number;
  readonly big: boolean;
  readonly lightbulb: boolean;
}

// Which half of the screen the mover is looking at. Not app state a peer can see and not a route:
// one mover, one phone, three things to do with it (docs/user-journeys.md, "Mover"). It lives on
// the store rather than in a hook because everything on this screen does (INVARIANTS rule 6).
export type MoverPanel = 'moves' | 'court' | 'audit';

export class MoverStore {
  // The court this mover is working. Undefined until they tap one, with no default: movers roam,
  // and picking one for them would attribute their first votes to a court they are not standing on.
  private _court: Court | undefined;
  private _panel: MoverPanel = 'moves';
  // The two confirm flows. A strike-off removes a player from every coach screen in the hall and a
  // reinstatement puts them back on a court the mover names, so neither happens on one tap of a row
  // in a list the mover is reading while walking.
  private _strikeCandidate: PlayerNumber | undefined;
  private _reinstateCandidate: PlayerNumber | undefined;

  // The last write that reached the gateway and failed, held until the next write that LANDS. The
  // sequencing is CoachStore's, and it is here for the same reason: writes overlap, the SDK throws
  // OfflineError at ENTRY so a tap taken while a slow write is still in flight can settle FIRST,
  // and without a sequence number the slow ack would clear a newer failure and leave a dropped
  // tick-off with nothing on the screen to say so.
  private _failedMessage: string | undefined;
  private _issued = 0;
  private _failedSeq: number | undefined;

  constructor(
    private readonly gateway: ICoachGateway,
    private readonly logger: ILogger,
    // Optional, and last, so the two-argument construction the contract pins still compiles. Same
    // default and same reason as CoachStore's: the SDK throws at the wire boundary on an
    // idempotency key that is not a canonical hyphenated UUID.
    private readonly idGen: IIdGen = new UuidV7Gen(),
  ) {
    // The three lists are compared STRUCTURALLY, for the reason CoachStore's `rows` is: the fold is
    // one observable ref, so every vote in the building invalidates them, and a plain computed
    // would hand the screen a fresh array — and a fresh set of row identities — for each one. The
    // mover is reading numbers off a list while walking; it may not repaint under their thumb
    // because somebody upvoted a player on a court they cannot see.
    makeAutoObservable<MoverStore, '_issued' | '_failedSeq'>(this, {
      _issued: false,
      _failedSeq: false,
      outstandingRows: computedStruct,
      auditRows: computedStruct,
      courtRows: computedStruct,
      returningRows: computedStruct,
      courtCounts: computedStruct,
    });
  }

  get court(): Court | undefined {
    return this._court;
  }

  // Live active headcount per court, on the picker buttons: the mover's whole job is evening the
  // hall out, so the numbers they are balancing belong on the thing they tap. Same derivation the
  // front desk shows, structural so a vote that moves no one repaints nothing.
  get courtCounts(): Record<Court, number> {
    return courtCounts(this.gateway.sessionState);
  }

  get panel(): MoverPanel {
    return this._panel;
  }

  // The tick's list minus the rows already actioned, in the order the admin device computed them.
  // Never re-sorted: the list is immutable from the moment it appears, so it does not shift under
  // the mover's thumb while voting continues underneath (docs/rotation.md).
  //
  // "Already actioned" is read off the fold rather than remembered here, so a row another mover
  // ticked off disappears from this phone too. A row whose player is no longer on its from-court
  // has been dealt with by somebody, and a row whose player went home has been dealt with by the
  // player.
  get outstandingRows(): readonly MoveRow[] {
    const list = latestMoveList(this.gateway.sessionState);
    if (list === undefined) return [];
    return list.moves.filter((row) => this.stillOn(row.player, row.from));
  }

  // Every active player and their court, all four courts, ascending by number. Movers see
  // everything, unlike coaches. `players` is already in number order, so the filter alone
  // preserves it — the number is what is written on the player's arm in marker, and it is what the
  // mover is reading off.
  get auditRows(): readonly MoverAuditRow[] {
    const rows: MoverAuditRow[] = [];
    for (const player of this.gateway.sessionState.players) {
      if (player.struckOff) continue;
      rows.push({ number: player.number, court: player.court });
    }
    return rows;
  }

  // The open court's players, for voting on behalf of a coach who cannot work a phone. Ascending by
  // number and never by score, exactly as the coach's own screen is: a list that re-sorts as votes
  // land turns every burst into mis-taps.
  get courtRows(): readonly MoverCourtRow[] {
    const court = this._court;
    if (court === undefined) return [];
    const rows: MoverCourtRow[] = [];
    for (const player of this.gateway.sessionState.players) {
      if (player.struckOff || player.court !== court) continue;
      rows.push({
        number: player.number,
        score: player.score,
        big: player.big,
        lightbulb: player.lightbulb,
      });
    }
    return rows;
  }

  // Players who went home, for the reinstate picker. Empty on the ordinary day, which is why it
  // sits under the audit rather than beside the move list.
  get returningRows(): readonly PlayerNumber[] {
    return struckOffPlayers(this.gateway.sessionState).map((player) => player.number);
  }

  // Which player the mover has asked to strike off, awaiting a confirm.
  get strikeCandidate(): PlayerNumber | undefined {
    return this._strikeCandidate;
  }

  // Which struck-off player the mover is reinstating, awaiting a court.
  get reinstateCandidate(): PlayerNumber | undefined {
    return this._reinstateCandidate;
  }

  // Ticking a row off MOVES a player, so it needs the session running as well as a connection. The
  // roster does not: players arrive early and leave late, and a strike-off around a stopped session
  // is a real thing that happens (docs/roster.md).
  get canMove(): boolean {
    return sessionStatus(this.gateway.sessionState) === 'running' && this.gateway.connectionOk;
  }

  // The mover votes on a coach's behalf, so the mover's votes are gated exactly as the coach's are.
  // The contract left this open; consistency decided it, and a vote cast before the session starts
  // makes no more sense from a mover's thumb than from a coach's
  // (session/contracts/p4-rotation.md, the amendment at the foot).
  get canVote(): boolean {
    return this.canMove;
  }

  get canEditRoster(): boolean {
    return this.gateway.connectionOk;
  }

  // Why a write cannot land right now. Undefined when the screen is working. Derived, not set by a
  // tap: a mover should be able to see why a button is dead without pressing it first.
  get notice(): string | undefined {
    // The connection first, and for CoachStore's reason: an offline device is reading a fold that
    // stopped arriving, so its idea of the session status is the one thing on this screen that may
    // already be wrong, and offline is the half a human in a hall can act on.
    if (!this.gateway.connectionOk) {
      return 'Offline. Nothing this screen sends can reach the session until this device is back on the wifi.';
    }
    const status = sessionStatus(this.gateway.sessionState);
    if (status === 'scheduled') {
      return 'The session has not started. Moves and votes open when the admin starts it; strike-off and reinstate work now.';
    }
    if (status === 'stopped') {
      return 'The session is stopped. Moves and votes are closed, and reopen if the admin starts it; strike-off and reinstate still work.';
    }
    return undefined;
  }

  // The last write that did not land. A silently dropped tick-off leaves a player standing on the
  // wrong court with the screen saying they moved (docs/sync.md).
  get failedMessage(): string | undefined {
    return this._failedMessage;
  }

  // A view change with one side effect: it decides which court this mover's own votes count
  // against (docs/rotation.md). No event, no write.
  openCourt = (court: Court): void => {
    this._court = court;
  };

  openPanel = (panel: MoverPanel): void => {
    this._panel = panel;
  };

  // The mover votes on behalf of a coach who cannot work a phone. Same semantics as the coach's
  // own tap: one execute, a fresh idempotency key, and nothing optimistic.
  voteFor = async (player: PlayerNumber, direction: 'up' | 'down'): Promise<void> => {
    await this.write({ kind: 'voteCast', payload: { player, direction } }, this.canVote, 'vote');
  };

  // The two marks, on behalf of the same coach the mover is voting for. BIG is a centre, the
  // lightbulb is basketball IQ, and the screen calls it IQ (docs/scoring.md, "Attributes").
  toggleBig = async (player: PlayerNumber): Promise<void> => {
    await this.toggle(player, 'big');
  };

  toggleLightbulb = async (player: PlayerNumber): Promise<void> => {
    await this.toggle(player, 'lightbulb');
  };

  // The flag's current value comes out of the FOLD on every tap, never out of a memory of what
  // this device last sent: a coach on this court may have cleared it in between, and a store
  // flipping its own last value would send `true` again and stick the flag on. The event carries
  // the RESULTING value rather than a flip instruction, so a mover and a coach tapping in the same
  // moment do not cancel into a no-op (docs/scoring.md). Same derivation as CoachStore's, because
  // it is the same event on the same aggregate from a second thumb.
  //
  // Gated on `canVote` for the reason the amendment at the foot of p4-rotation.md gives about
  // `voteFor`: the mover acts for a coach, and a coach cannot mark before the whistle either.
  private toggle = async (player: PlayerNumber, attribute: Attribute): Promise<void> => {
    const current = playerAttributes(this.gateway.sessionState, player)[attribute];
    await this.write(
      { kind: 'attributeSet', payload: { player, attribute, value: !current } },
      this.canVote,
      attribute === 'big' ? 'BIG mark' : 'IQ mark',
    );
  };

  // Ticking the row off is what MOVES the player: both coach screens update on it. `mean` always —
  // `bottom` and `top` exist only for the 60-minute tick, which no mover is involved in.
  tickOff = async (row: MoveRow): Promise<void> => {
    await this.write(
      { kind: 'playerMoved', payload: { player: row.player, toCourt: row.to, placement: 'mean' } },
      this.canMove,
      'move',
    );
  };

  // A player who went home. They vanish from the coach screens and the report, and the court's
  // headcount shrinks, which the next tick's rebalance absorbs (docs/roster.md).
  strikeOff = async (player: PlayerNumber): Promise<void> => {
    this._strikeCandidate = undefined;
    await this.write(
      { kind: 'playerStruckOff', payload: { player } },
      this.canEditRoster,
      'strike-off',
    );
  };

  // They came back. The mover nominates the court they are walking onto, which is not necessarily
  // the one they left.
  reinstate = async (player: PlayerNumber, court: Court): Promise<void> => {
    this._reinstateCandidate = undefined;
    await this.write(
      { kind: 'playerReinstated', payload: { player, court } },
      this.canEditRoster,
      'reinstate',
    );
  };

  // The confirm flows. Both are pure view state and neither writes: the write is the method above.
  askStrikeOff = (player: PlayerNumber): void => {
    this._strikeCandidate = player;
    this._reinstateCandidate = undefined;
  };

  askReinstate = (player: PlayerNumber): void => {
    this._reinstateCandidate = player;
    this._strikeCandidate = undefined;
  };

  cancel = (): void => {
    this._strikeCandidate = undefined;
    this._reinstateCandidate = undefined;
  };

  // ONE execute per tap and no ladder. Every write on this screen is `occ: false`, so there is
  // nothing to climb, and sync.md forbids queues and replays: a write that failed is gone and the
  // human is told.
  private write = async (event: SessionEvent, allowed: boolean, what: string): Promise<void> => {
    if (!allowed) {
      // No gateway call, nothing thrown, and `notice` already says why. A dead button that logged
      // an error every time a mover leaned on it would bury the real failures.
      this.logger.debug(`mover: ${event.kind} refused, ${what} is closed`);
      return;
    }
    // Taken BEFORE the await, so the number orders the writes by when they LEFT rather than by
    // when they came back.
    const seq = ++this._issued;
    try {
      await this.gateway.execute(event, { idempotencyKey: this.idGen.next() });
      // Nothing optimistic on purpose: the ack is not the fold. A row struck off the list here
      // would tell the mover a player is on a court no other device in the hall has heard about.
      this.clearFailure(seq);
    } catch (cause) {
      // Translate first: the gateway is a boundary over foreign code, and a bug inside it arrives
      // as a TypeError that must not be absorbed as if it were a gateway outage (rule 17).
      const failure =
        cause instanceof DomainError ? cause : new ServerError(`The ${what} failed.`, { cause });
      this.logger.warn(`mover: ${event.kind} failed`, failure.tag);
      this.showFailure(moverMessage(failure, what), seq);
    }
  };

  // A row is outstanding while its player is still on the court the row starts from. A player who
  // has been moved, by this mover or another, or who has gone home, is not the mover's problem.
  private stillOn(player: PlayerNumber, court: Court): boolean {
    // By number, not by index: a pre-allocated day's `players` is sorted by number and has gaps in
    // it, so `players[n - 1]` is somebody else (docs/roster.md).
    const record = playerRecord(this.gateway.sessionState, player);
    return record !== undefined && !record.struckOff && record.court === court;
  }

  // Newest failure wins. An older write coming back late must not paint over the failure of one
  // that left after it, because the message says what did not happen and the mover's next move
  // depends on which write it is about.
  private showFailure(message: string, seq: number): void {
    if (this._failedSeq !== undefined && seq < this._failedSeq) return;
    this._failedMessage = message;
    this._failedSeq = seq;
  }

  // A write that LANDS clears the banner, and only the banner of a write that left before it.
  private clearFailure(seq: number): void {
    if (this._failedSeq !== undefined && this._failedSeq > seq) return;
    this._failedMessage = undefined;
    this._failedSeq = undefined;
  }
}

// What the mover is told when a write did not land. Every message says what did NOT happen, in the
// mover's own terms, because their next move is deciding whether to tap again — and it never says
// "tap again" where tapping again cannot work.
function moverMessage(failure: DomainError, what: string): string {
  if (failure instanceof OfflineError) return `Offline. That ${what} did not happen.`;
  if (failure instanceof UpdateRequiredError) {
    return `This app is out of date. That ${what} did not happen — reload the page.`;
  }
  return `That ${what} did not reach the session. Nothing changed — tap again.`;
}

export const TOKEN_MoverStore = token<MoverStore>('MoverStore');
